import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Typography,
} from '@mui/material';
import { reportNumber, reportRate } from './clientPurchaseReportUtils';

const numberFormatter = new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 0,
});

function ValidationMetric({ label, metric, color, hasData, loading }) {
  const value = Number(metric?.value || 0);
  const share = metric?.share;
  const shareLabel = share === null || typeof share === 'undefined'
    ? '—'
    : `${Number(share).toFixed(1)}%`;

  return (
    <Box
      role="group"
      aria-label={`${label}: ${hasData ? numberFormatter.format(value) : 'not available'}, ${hasData ? shareLabel : 'not available'}`}
      sx={{
        minWidth: 0,
        pl: { xs: 0.8, sm: 1 },
        borderLeft: `3px solid ${color}`,
      }}
    >
      <Typography sx={{ color: '#475569', fontSize: { xs: '0.68rem', sm: '0.72rem' }, fontWeight: 700 }}>
        {label}
      </Typography>
      {loading && !hasData ? (
        <Skeleton width="72%" height={30} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: { xs: 0.4, sm: 0.65 } }}>
          <Typography sx={{ color: '#0f172a', fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 850, lineHeight: 1.3 }}>
            {hasData ? numberFormatter.format(value) : '—'}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {hasData ? shareLabel : '—'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ClientsAccountValidation({ validation, lifecycle, status }) {
  const hasData = Boolean(validation);
  const loading = status === 'loading';

  return (
    <Card
      component="section"
      aria-labelledby="account-validation-heading"
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 3px 12px rgba(15, 23, 42, 0.06)',
      }}
    >
      <CardContent sx={{ p: { xs: '12px !important', sm: '14px 16px !important' } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(180px, 0.8fr) minmax(0, 2fr)' },
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          <Box minWidth={0}>
            <Typography id="account-validation-heading" sx={{ color: '#111827', fontWeight: 800, fontSize: '0.92rem' }}>
              Account validation
            </Typography>
            <Typography sx={{ mt: 0.1, color: '#64748b', fontSize: '0.72rem' }}>
              Current status of selected registrations
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: { xs: 0.75, sm: 1 },
              minWidth: 0,
            }}
          >
            <ValidationMetric
              label="Validated"
              metric={validation?.validated}
              color="#059669"
              hasData={hasData}
              loading={loading}
            />
            <ValidationMetric
              label="Not validated"
              metric={validation?.not_validated}
              color="#d97706"
              hasData={hasData}
              loading={loading}
            />
            <ValidationMetric
              label="Suspended"
              metric={validation?.suspended}
              color="#64748b"
              hasData={hasData}
              loading={loading}
            />
          </Box>
        </Box>
      {lifecycle && (
        <Box component="section" aria-label="Account validation reminders" sx={{ borderTop: '1px solid #e2e8f0', mt: 1.25, pt: 1.25 }}>
          <Typography component="h3" sx={{ color: '#475569', fontSize: '0.78rem', fontWeight: 700, mb: 1 }}>Reminders sent in selected period</Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
            {[
              ['Reminded clients', reportNumber(lifecycle.reminded)],
              ['Validated after reminder', reportNumber(lifecycle.validated_after_reminder)],
              ['Erased clients', reportNumber(lifecycle.erased)],
              ['Reminder conversion', reportRate(lifecycle.reminder_conversion == null ? null : lifecycle.reminder_conversion * 100)],
            ].map(([label, value]) => (
              <Box key={label} role="group" aria-label={`${label}: ${value}`} sx={{ minWidth: 0 }}>
                <Typography component="dt" sx={{ color: '#475569', fontSize: '0.75rem' }}>{label}</Typography>
                <Typography component="dd" sx={{ m: 0, color: '#0f172a', fontSize: '1.1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ mt: 1, color: '#475569', fontSize: '0.75rem' }}>
            Current outcomes of these reminders, regardless of account age.
          </Typography>
        </Box>
      )}
      </CardContent>
    </Card>
  );
}

export default ClientsAccountValidation;
